import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { MarkerClusterer } from '@react-google-maps/api'
import moment from 'moment/moment'
import PropTypes from 'prop-types'

import find from 'lodash/find'
import forEach from 'lodash/forEach'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import map from 'lodash/map'
import toLower from 'lodash/toLower'
import uniqueId from 'lodash/uniqueId'

import { Card, CardContent, Grid, Typography } from '@material-ui/core'

import { useDialog } from '@galo-monitor/dialog'
import { useT } from '@galo-monitor/i18n'
import {
  getStatesOfCountry,
  getCitiesOfState
} from '@galo-monitor/services/apis/countryStateCityApi'
import api from '@galo-monitor/services/apis/galoMonitorApi/api'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { CompanyActions } from '@galo-monitor/stores/company'
import { ContextActions } from '@galo-monitor/stores/context'
import { FarmActions } from '@galo-monitor/stores/farm'
import { selectLanguage } from '@galo-monitor/stores/language/selectorLanguage'
import fonts from '@galo-monitor/styles/fonts'
import { momentBackDateFormat, momentFriendlyDateFormat, momentFriendlyDateUSFormat } from '@galo-monitor/utils/dates'
import { getPolygonCenter } from '@galo-monitor/utils/maps'
import Button from '@galo-monitor/web-components/Button'
import InputDate from '@galo-monitor/web-components/InputDate'
import InputSelect from '@galo-monitor/web-components/InputSelect'
import GoogleMaps from '@galo-monitor/web-components/Maps/Google'
import PopupPolygon from '@galo-monitor/web-components/Maps/Google/components/PopupPolygon'
import withGoogleMaps from '@galo-monitor/web-components/Maps/Google/hoc/withGoogleMaps'
import Screen from '@galo-monitor/web-components/Screen'
import ErrorModal from '@galo-monitor/web-containers/modals/ErrorModal'
import { useRoutes } from '@galo-monitor/web-containers/routes/authenticated'

import useStyles from './styles'
import { CardContainer, Container } from './theme'

const FILTERS_INITIAL_STATE = {
  country: '',
  state: '',
  city: '',
  culture: '',
  date: '',
  cultivation_method: ''
}

const GOOGLE_EARTH_CENTER = {
  lat: -6.4762323,
  lng: -25.3525844
}

const DEFAULT_ZOOM = 3

const OverviewScreen = ({ isGoogleMapsLoaded }) => {
  const classes = useStyles()
  const t = useT()
  const { createDialog } = useDialog()
  const { routes } = useRoutes()
  const dispatch = useCallback(useDispatch(), [])

  const { selected } = useSelector(selectLanguage)

  const [filters, setFilters] = useState(FILTERS_INITIAL_STATE)
  const [stateOptions, setStateOptions] = useState([])
  const [cityOptions, setCityOptions] = useState([])
  const [startDate, setStartDate] = useState(moment().format(momentBackDateFormat))
  const [endDate, setEndDate] = useState(moment().format(momentBackDateFormat))
  const [loading, setLoading] = useState(false)
  const [filteredData, setFilteredData] = useState({})
  const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM)

  const mounted = useRef(true)

  const { data: countries } = useGaloMonitorApi('/shared/countries')
  const { data: cultures } = useGaloMonitorApi(filters.country ? `/shared/cultures/${ filters.country }` : null)

  const countryOptions = useMemo(
    () => map(countries, ({ name, id }) => ({
      label: name,
      value: String(id)
    })),
    [countries]
  )

  const cultureOptions = useMemo(
    () => !isEmpty(cultures)
      ? map(cultures, ({ name, id }) => ({
        label: name,
        value: String(id)
      }))
      : [],
    [cultures]
  )

  const cultivationMethodsOptions = useMemo(
    () => [
      {
        label: t('cultivation method label conventional'),
        value: 'conventional'
      },
      {
        label: t('cultivation method label organic'),
        value: 'organic'
      },
      {
        label: t('cultivation method label hydroponic'),
        value: 'hydroponic'
      }
    ],
    [t]
  )

  const mapCenter = useMemo(() => {
    if (filteredData?.results && !isEmpty(filteredData?.results)) {
      let coordinates = []
      forEach(filteredData?.results, plot => {
        const formattedCoordinates = map(
          plot.coordinates,
          ({ lat: latitude, long: longitude }) => ({ latitude, longitude })
        )
        coordinates = [...coordinates, ...formattedCoordinates]
      })

      return getPolygonCenter(coordinates)
    }

    setMapZoom(DEFAULT_ZOOM)
    return GOOGLE_EARTH_CENTER
  }, [filteredData.results])

  const plots = useMemo(() => {
    if (!isEmpty(filteredData?.results)) {
      return map(filteredData.results, plot => {
        const { coordinates } = plot
        const paths = map(coordinates, coord => ({ lat: +coord.lat, lng: +coord.long }))

        const plotStartDate = moment(plot.dateInitial)
          .format(selected === 'pt' ? momentFriendlyDateFormat : momentFriendlyDateUSFormat)
        const plotEndDate = moment(plot.dateFinish)
          .format(selected === 'pt' ? momentFriendlyDateFormat : momentFriendlyDateUSFormat)

        return {
          id: plot.idPlot,
          culture: plot.nameCulture,
          farmId: plot.idFarm,
          harvest: plot.nameHarvest,
          paths,
          period: t('from {start} to {finish}', { start: plotStartDate, finish: plotEndDate }),
          plot: plot.namePlot,
          property: plot.nameProperty,
          color: plot.colorHarvest
        }
      })
    }

    return []
  }, [filteredData.results, selected, t])

  const crumbs = useMemo(() => [
    {
      title: routes.overview.title,
      key: 'farms-overview-screen',
      url: '/overview',
      active: true
    }
  ], [routes.overview.title])

  const disableFetchButton = useMemo(() => {
    if (isEmpty(filters.country) || isEmpty(filters.date))
      return true
    return false
  }, [filters])

  const getSelectedCountry = useCallback(
    countryId => find(countries, ({ id }) => +id === +countryId),
    [countries]
  )

  const updateFilters = useCallback(
    (name, value) => setFilters(prevState => ({ ...prevState, [name]: value })),
    []
  )

  const resetFilters = useCallback(
    () => setFilters(({ date }) => ({ ...FILTERS_INITIAL_STATE, date })),
    []
  )

  const accessFarm = useCallback((farm) => {
    dispatch(CompanyActions.setDefaultFarm(
      farm.id,
      () => {
        dispatch(FarmActions.setCurrentFarm(farm))
        dispatch(ContextActions.initContextFarm(farm.id))
      },
      () => createDialog({
        id: 'access-farm-error-dialog',
        Component: ErrorModal,
        props: {
          title: t('could not {action} the {this}', {
            action: toLower(t('to access')),
            gender: 'female',
            howMany: 1,
            this: `${ t('farm', { howMany: 1 }) } ${ farm.id }`
          })
        }
      })
    ))
  }, [createDialog, dispatch, t])

  const onError = useCallback(
    () => {
      setLoading(false)
      createDialog({
        id: 'error-overview-fetch',
        Component: ErrorModal,
        props: {
          title: `${ t('could not {action} the {this}', {
            howMany: 1,
            gender: 'male',
            action: toLower(t('to filter')),
            this: toLower(t('farm', { howMany: 1 }))
          })  }.`,
          message: t('datagrid fetch loadh error')
        }
      })
    },
    [createDialog, t]
  )

  const fetchMapData = useCallback(async () => {
    try {
      setLoading(true)
      const { data } = await api.get('/companies/farms/properties/filter', { params: filters })

      setFilteredData(data)
      setLoading(false)
    } catch(err) {
      onError(err)
    }
  }, [filters, onError])

  const handleCountryChange = useCallback(
    (value, _, reason) => {
      if (reason === 'clear') {
        updateFilters('country', '')
      }

      if (value) {
        resetFilters()
        const selectedCountry = getSelectedCountry(value)
        let data = getStatesOfCountry(selectedCountry.isoCode)
        if (mounted.current) {
          updateFilters('country', String(value))
          data = map(data, ({ name }) => ({ label: name, value: name }))
          setStateOptions(data)
        }
      } else {
        setStateOptions([])
      }
      setCityOptions([])
    },
    [getSelectedCountry, resetFilters, updateFilters]
  )

  const handleStateChange = useCallback(
    (value, _, reason) => {
      if (reason === 'clear') {
        updateFilters('state', '')
      }

      updateFilters('city', '')

      if (value) {
        const selectedCountry = getSelectedCountry(filters.country)
        const statesOfCountry = getStatesOfCountry(selectedCountry.isoCode)
        const selectedState = find(statesOfCountry, state => state.name === value)
        let data = getCitiesOfState(selectedCountry.isoCode, selectedState?.isoCode ?? 'PR')

        if (mounted.current) {
          updateFilters('state', value)
          data = map(data, ({ name }) => ({ label: name, value: name }))
          setCityOptions(data)
        }
      } else {
        setCityOptions([])
      }
    },
    [filters.country, getSelectedCountry, updateFilters]
  )

  const handleCityChange = useCallback(
    (value, _, reason) => {
      if (reason === 'clear') {
        updateFilters('city', '')
      }

      if (value) {
        if (mounted.current) {
          updateFilters('city', value)
        }
      } else {
        setCityOptions([])
      }
    },
    [updateFilters]
  )

  const handleCultureChange = useCallback(
    (value, _, reason) => {
      if (reason === 'clear') {
        updateFilters('culture', '')
      }

      if (value) {
        if (mounted.current) {
          updateFilters('culture', value)
        }
      }
    },
    [updateFilters]
  )

  const handleCultivationMethodChange = useCallback(
    (value, _, reason) => {
      if (reason === 'clear') {
        updateFilters('cultivation_method', '')
      }

      if (value) {
        if (mounted.current) {
          updateFilters('cultivation_method', value)
        }
      }
    },
    [updateFilters]
  )

  const overviewFilters = useMemo(() => [
    {
      name: 'country',
      type: 'select',
      label: t('country', { howMany: 1 }),
      options: countryOptions,
      onChange: handleCountryChange,
      disabled: loading
    },
    {
      name: 'state',
      type: 'select',
      label: t('state', { howMany: 1 }),
      options: stateOptions,
      onChange: handleStateChange,
      disabled: loading
    },
    {
      name: 'city',
      type: 'select',
      label: t('city', { howMany: 1 }),
      options: cityOptions,
      onChange: handleCityChange,
      disabled: loading
    },
    {
      name: 'culture',
      type: 'select',
      label: t('culture', { howMany: 1 }),
      options: cultureOptions,
      onChange: handleCultureChange,
      disabled: loading
    },
    {
      name: 'cultivation_method',
      type: 'select',
      label: t('cultivation method'),
      options: cultivationMethodsOptions,
      onChange: handleCultivationMethodChange,
      disabled: loading
    },
    {
      name: 'start_date',
      type: 'date',
      label: t('start date'),
      value: startDate,
      onChange: setStartDate,
      disabled: loading
    },
    {
      name: 'end_date',
      type: 'date',
      label: t('end date'),
      value: endDate,
      onChange: setEndDate,
      disabled: loading
    }
  ], [t,
    countryOptions,
    handleCountryChange,
    loading,
    stateOptions,
    handleStateChange,
    cityOptions,
    handleCityChange,
    cultureOptions,
    handleCultureChange,
    cultivationMethodsOptions,
    handleCultivationMethodChange,
    startDate,
    endDate
  ])

  const overviewCards = useMemo(() => [
    {
      title: t('property', { howMany: 2 }),
      valueKey: 'totalProperty'
    },
    {
      title: t('plot', { howMany: 2 }),
      valueKey: 'totalPlots'
    },
    {
      title: t('total area'),
      valueKey: 'totalArea',
      subtext: t('hectare', { howMany: 2 })
    }
  ], [t])

  useEffect(() => {
    updateFilters('date', `${ startDate },${ endDate }`)
  }, [startDate, endDate, updateFilters])

  useEffect(() => () => {
    mounted.current = false
  }, [])

  return (
    <>
      <Screen
        title={ routes.overview.title }
        breadcrumbs={ crumbs }
        withCard
      >
        <Grid
          container
          spacing={ 2 }
          alignItems="center"
          style={ { marginTop: 5, marginBottom: 5 } }
        >
          {map(overviewFilters, filter => (
            <Grid
              key={ uniqueId(filter.name) }
              item
              xs={ 12 }
              sm={ 6 }
              lg
              { ...filter.grid }
            >
              {
                filter.type === 'select' ? (
                  <InputSelect
                    name={ filter.name }
                    label={ filter.label }
                    value={ filters[filter.name] }
                    onChange={ filter.onChange }
                    options={ filter.options }
                    disabled={ filter.disabled }
                    autoComplete="off"
                    detached
                    fullWidth
                  />
                ) : (
                  <InputDate
                    label={ filter.label }
                    name={ filter.name }
                    value={ filter.value }
                    onChange={ filter.onChange }
                    locale={ selected }
                    detached
                    fullWidth
                  />
                )
              }
            </Grid>
          ))}
          <Grid
            item
            xs={ 12 }
            sm={ 12 }
            md={ 12 }
            lg="auto"
          >
            <Container>
              <Button
                id="fetch"
                color="primary"
                onClick={ fetchMapData }
                disabled={ loading || disableFetchButton }
                fullWidth
              >
                { t('to filter') }
              </Button>
            </Container>
          </Grid>
        </Grid>
        <GoogleMaps
          zoom={ mapZoom }
          center={ mapCenter }
          isLoaded={ isGoogleMapsLoaded }
          mapContainerStyle={ {
            width: '100%',
            height: '60vh'
          } }
        >
          <MarkerClusterer>
            {
              clusterer =>
                !isEmpty(plots)
                  ? map(plots, ({ id, paths, farmId, color, ...plot }) => (
                    <PopupPolygon
                      key={ uniqueId('polygon') }
                      paths={ paths }
                      clusterer={ clusterer }
                      options={ {
                        fillOpacity: 0.2,
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                        fillColor: color,
                        strokeColor: color
                      } }
                    >
                      <div style={ { padding: '0 10px' } }>
                        <>
                          {
                            map(Object.entries(plot), ([key, value]) => (
                              <div key={ uniqueId('polygon-item') }>
                                <Typography color="textPrimary" variant="body2" component="span" style={ { fontWeight: fonts.fontWeight.bold } }>
                                  {`${ t(key, { howMany: 1 }) }: `}
                                </Typography>
                                <Typography color="textSecondary" variant="body2" component="span" style={ { fontWeight: fonts.fontWeight.bold } }>
                                  {value}
                                </Typography>
                              </div>
                            ))
                          }
                          <Typography
                            color="primary"
                            variant="body2"
                            onClick={ () => accessFarm({ id: farmId, name: plot.property }) }
                            style={ {
                              fontWeight: fonts.fontWeight.bold,
                              textDecoration: 'underline',
                              cursor: 'pointer'
                            } }
                          >
                            {`${ t('to access') } ${ t('property', { howMany: 1 }) }`}
                          </Typography>
                        </>
                      </div>
                    </PopupPolygon>
                  )) : []
            }
          </MarkerClusterer>
        </GoogleMaps>
      </Screen>
      <Grid container spacing={ 2 } style={ { marginTop: 0 } }>
        {map(overviewCards, card => (
          <Grid
            key={ uniqueId(card.valueKey) }
            item
            xs={ 12 }
            sm={ 6 }
            md
            style={ { marginTop: 16 } }
          >
            <Card>
              <CardContent>
                <Typography
                  className={ classes.cardTitle }
                  variant="body1"
                  gutterBottom
                >
                  {card.title}
                </Typography>
                <CardContainer>
                  <Typography className={ classes.cardValue } variant="h5">
                    { isNil(filteredData?.[card.valueKey]) ? '-' : filteredData[card.valueKey] }
                  </Typography>
                  {
                    card.subtext
                      ? (
                        <Typography
                          className={ classes.cardValue }
                          variant="subtitle2"
                          color="textSecondary"
                        >
                          { card.subtext }
                        </Typography>
                      ) : null
                  }
                </CardContainer>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </>
  )
}

OverviewScreen.propTypes = {
  isGoogleMapsLoaded: PropTypes.bool
}

OverviewScreen.defaultProps = {
  isGoogleMapsLoaded: false
}

export default withGoogleMaps(OverviewScreen)
